import React from 'react';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { Ritual } from '../../@types/Calculations';
import { SimState, StateCalculation } from '../../@types/State';
import { rituals } from '../../helpers/GameAgeData';
import { numberToStringWithCommas } from '../../helpers/NumberFormatting';

const getTableRowDualValue = (name: string, v1: number) => {
  return (
    <TableRow
      key={name}
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell component='th' scope='row'>
        {name}
      </TableCell>
      <TableCell align='right'>{v1}</TableCell>
    </TableRow>
  );
};

interface Props {
  simState: SimState;
  gameState: StateCalculation;
  updateSimState: (field: string, value: any) => void;
}

const TrainingTick: React.FC<Props> = ({
  simState,
  gameState,
  updateSimState,
}) => {
  const handleSectionChange = (
    section: 'buildPercentages' | 'science' | 'training',
    field: string,
    e: any
  ) => {
    const newSection = {
      ...simState[section],
      [field]: parseFloat(e.target.value || 0),
    };
    updateSimState(section, newSection);
  };

  const ritualSelector = (field: 'ritual', arr: Ritual[]) => {
    return (
      <FormControl fullWidth size='small'>
        <InputLabel>{field}</InputLabel>
        <Select
          labelId={`${field}-label`}
          value={simState[field]}
          label={JSON.stringify(field)}
          onChange={(e) => {
            updateSimState(field, e.target.value);
          }}
        >
          {arr.map((item, i) => (
            <MenuItem key={i} value={JSON.stringify(item)}>
              {item.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const buildingSelector = (field: string) => {
    return (
      <Grid item xs={1}>
        <TextField
          fullWidth
          size='small'
          type='number'
          label={field}
          InputProps={{ inputProps: { min: 0, max: 100 } }}
          value={simState.buildPercentages[field]}
          onChange={(e) => handleSectionChange('buildPercentages', field, e)}
        />
      </Grid>
    );
  };

  return (
    <Box mx={2}>
      <Typography variant='body1'>
        Based on your current build, at the start of the training tick you have{' '}
        $ {numberToStringWithCommas(gameState.money)} available for Conversion,
        Dragon, and Training with a current Draft of{' '}
        {gameState.calculatedEconomy.currentDraft}%.
      </Typography>
      <Typography variant='body1'>
        You are keeping ${' '}
        {numberToStringWithCommas(gameState.trainingCalc.conversionCost)} for
        converting {simState.training.razePcnt}% of your buildings to war build.
      </Typography>
      <Typography variant='body1'>
        You are keeping {simState.training['gcpa']} GCpa aside for dragon
        funding as you exit eowcf, a total of ${' '}
        {numberToStringWithCommas(gameState.trainingCalc.dragonMoney)}.
      </Typography>
      <Typography variant='body1'>
        You have {gameState.armyCalc.soldiers} soldiers currently and will
        recruit an additional {gameState.recruitsNeeded} to reach your target
        draft of {simState.targetDraft}% with{' '}
        {gameState.armyCalc.soldiers + gameState.recruitsNeeded} soldiers to
        train.
      </Typography>
      <Box mt={2}>
        <Grid
          container
          spacing={2}
          mt={1}
          columns={{ xs: 2, sm: 3, md: 6, xl: 12 }}
        >
          <Grid item xs={1}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='draft target %'
              InputProps={{ inputProps: { min: 20, max: 100 } }}
              value={simState.targetDraft}
              onChange={(e) =>
                updateSimState('targetDraft', parseInt(e.target.value))
              }
            />
          </Grid>
          <Grid item xs={1}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='gold'
              value={simState.money}
              InputProps={{ inputProps: { min: 0 } }}
              onChange={(e) =>
                updateSimState('money', parseInt(e.target.value))
              }
            />
          </Grid>
          <Grid item xs={1}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='rawTpa'
              value={simState.training.rawTpa}
              InputProps={{ inputProps: { min: 0, max: 25 } }}
              onChange={(e) => handleSectionChange('training', 'rawTpa', e)}
            />
          </Grid>
          <Grid item xs={1}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='dspa'
              value={simState.training.dspa}
              InputProps={{ inputProps: { min: 0, max: 200 } }}
              onChange={(e) => handleSectionChange('training', 'dspa', e)}
            />
          </Grid>
          <Grid item xs={1}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='gcpa'
              value={simState.training.gcpa}
              InputProps={{ inputProps: { min: 0, max: 2000 } }}
              onChange={(e) => handleSectionChange('training', 'gcpa', e)}
            />
          </Grid>
          <Grid item xs={1}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='raze%'
              value={simState.training.razePcnt}
              InputProps={{ inputProps: { min: 0, max: 100 } }}
              onChange={(e) => handleSectionChange('training', 'razePcnt', e)}
            />
          </Grid>
          <Grid item xs={1}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='buildCredits'
              value={simState.training.buildCredits}
              InputProps={{ inputProps: { min: 0, max: 100000 } }}
              onChange={(e) =>
                handleSectionChange('training', 'buildCredits', e)
              }
            />
          </Grid>
          <Grid item xs={1}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='trainCredits'
              value={simState.training.trainCredits}
              InputProps={{ inputProps: { min: 0, max: 100000 } }}
              onChange={(e) =>
                handleSectionChange('training', 'trainCredits', e)
              }
            />
          </Grid>
          <Grid item xs={1}>
            {ritualSelector('ritual', rituals)}
          </Grid>
          <Grid item xs={1}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='ritualEffect'
              value={simState.ritEff}
              InputProps={{ inputProps: { min: 50, max: 200 } }}
              onChange={(e) =>
                updateSimState('ritEff', parseFloat(e.target.value))
              }
            />
          </Grid>
          {buildingSelector('armouries')}
        </Grid>
      </Box>
      <Box mt={2}>
        <Paper elevation={3} sx={{ p: 2 }}>
          <Typography variant='body2' gutterBottom>
            {gameState.trainingCalc.oSpecsToTrain > 0 ? 'Train' : 'Release'}{' '}
            {gameState.trainingCalc.oSpecsToTrain} OffSpecs
          </Typography>
          <Typography variant='body2' gutterBottom>
            {gameState.trainingCalc.dSpecToTrain > 0 ? 'Train' : 'Release'}{' '}
            {gameState.trainingCalc.dSpecToTrain} DefSpecs
          </Typography>
          <Typography variant='body2' gutterBottom>
            Train {gameState.trainingCalc.maxElitesToTrain} Elites
          </Typography>
          <Typography variant='body2' gutterBottom>
            {gameState.trainingCalc.thievesToTrain > 0 ? 'Train' : 'Release'}{' '}
            {gameState.trainingCalc.thievesToTrain} Thieves
          </Typography>
          <Typography variant='body2' gutterBottom>
            Final Army Size {gameState.trainingCalc.totalArmySizeAfterTrain}
          </Typography>
        </Paper>
      </Box>
      <Box mt={2}></Box>
      <TableContainer component={Paper}>
        <Table size='small' aria-label='a dense table'>
          <TableHead>
            <TableRow>
              <TableCell>Calcs</TableCell>
              <TableCell align='right'>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getTableRowDualValue(
              'Total Solds',
              gameState.recruitsNeeded +
                gameState.armyCalc.soldiers +
                gameState.armyCalc.soldiersTraining
            )}
            {getTableRowDualValue(
              'Construction Cost p/a',
              gameState.constructionCost
            )}
            {getTableRowDualValue('Raze Cost p/a', gameState.razeCost)}
            {getTableRowDualValue('Cost per thief', gameState.thiefCost)}
            {getTableRowDualValue('Cost per oSpec', gameState.oSpecCost)}
            {getTableRowDualValue('Cost per dSpec', gameState.dSpecCost)}
            {getTableRowDualValue('Cost per elite', gameState.eliteCost)}
            {getTableRowDualValue(
              'DSpecs Cost Total',
              gameState.trainingCalc.dSpecsTrainingCost
            )}
            {getTableRowDualValue(
              'OSpecs Cost Total',
              gameState.trainingCalc.oSpecsTrainingCost
            )}
            {getTableRowDualValue(
              'Elites Cost Total',
              gameState.trainingCalc.elitesTrainingCost
            )}
            {getTableRowDualValue(
              'Thieves Cost Total',
              gameState.trainingCalc.thievesTrainingCost
            )}
            {getTableRowDualValue(
              'Training Cost Total',
              gameState.trainingCalc.totalTrainingCost
            )}
            {getTableRowDualValue(
              `Total Conversion Cost (${simState.training['razePcnt']}%)`,
              gameState.trainingCalc.conversionCost
            )}
            {getTableRowDualValue(
              'Dragon GCs required',
              gameState.trainingCalc.dragonMoney
            )}
            {getTableRowDualValue(
              'GC Balance',
              gameState.trainingCalc.moneyAfterConversionAndTraining
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TrainingTick;
